/**
 * Display-side arithmetic for the marking panel.
 *
 * The **outcome itself is not computed here** — it comes from `computePerformanceOutcome`
 * in `@hockeytd/shared`, the single implementation the API, the report generator and this
 * screen all share, so a bar cannot mean one thing on the laptop and another on the wire.
 * What lives here is the presentation around it: the band label, the live Σweight, and the
 * translation between the wire's `MarkView` and the shared module's `CriterionMark`.
 *
 * Everything computed here is provisional. It exists so the figure moves the instant the
 * bar does; the server's reply to the write replaces it.
 */

import { computePerformanceOutcome, type CriterionMark, type MarkingCriterion } from "@hockeytd/shared";

import type { MarkView } from "../../api/types.js";

/** The five bands of `05-reporting-marking.md` §B3. */
export type OutcomeBand = "excellent" | "verygood" | "good" | "satis" | "below";

export interface BandDescriptor {
  band: OutcomeBand;
  /** The label, verbatim from §B3. */
  label: string;
  /** The mockup's class, from `official-profile-detail-v1.html`. */
  className: string;
}

const BANDS: readonly BandDescriptor[] = [
  { band: "excellent", label: "Excellent", className: "band--excellent" },
  { band: "verygood", label: "Very Good", className: "band--verygood" },
  { band: "good", label: "Good", className: "band--good" },
  { band: "satis", label: "Satisfactory", className: "band--satis" },
  { band: "below", label: "Below Standard", className: "band--below" },
];

/**
 * The band for an outcome, or null when there is none.
 *
 * Null is a real state, not zero: an unmarked panel has **no** Performance Outcome, and
 * §B9 is explicit that a loading or empty surface must never render a figure as though it
 * were a value.
 */
export function bandFor(outcome: number | null): BandDescriptor | null {
  if (outcome === null) return null;
  if (outcome >= 90) return BANDS[0] ?? null;
  if (outcome >= 80) return BANDS[1] ?? null;
  if (outcome >= 65) return BANDS[2] ?? null;
  if (outcome >= 50) return BANDS[3] ?? null;
  return BANDS[4] ?? null;
}

/** The wire's marks, in the shape `@hockeytd/shared` expects. */
export function toCriterionMarks(marks: readonly MarkView[]): CriterionMark[] {
  return marks.map((mark) => ({
    criterion_stable_key: mark.criterionStableKey,
    value: mark.value,
    na: mark.na,
  }));
}

/** The optimistic outcome, by the one shared formula. */
export function liveOutcome(
  criteria: readonly MarkingCriterion[],
  marks: readonly MarkView[],
): number | null {
  return computePerformanceOutcome(criteria, toCriterionMarks(marks));
}

/**
 * The panel foot's **live** Σweight: the set's total less the weight of every criterion
 * marked `N/A` (§B3 — "an `N/A` removes that criterion's weight from the denominator, and
 * the panel foot then reads the live Σweight rather than the constant 200").
 *
 * An *unmarked* criterion is a different thing and stays in this figure: it is out of the
 * outcome's own narrower denominator, but it has not been declared inapplicable.
 */
export function liveWeightTotal(
  criteria: readonly MarkingCriterion[],
  marks: readonly MarkView[],
): number {
  return criteria.reduce((total, criterion) => {
    const mark = marks.find((m) => m.criterionStableKey === criterion.criterion_stable_key);
    return mark?.na === true ? total : total + criterion.weight;
  }, 0);
}

/** Replace one criterion's mark in a mark list, adding it when it was unmarked. */
export function withMark(
  marks: readonly MarkView[],
  criterionStableKey: string,
  next: { value: number | null; na: boolean },
): MarkView[] {
  const others = marks.filter((mark) => mark.criterionStableKey !== criterionStableKey);
  // Clearing a mark removes the row — "unmarked" is the absence of a row, here as in
  // Postgres (see `apps/api/src/repositories/marks.ts`).
  if (next.value === null && !next.na) return others;
  return [...others, { criterionStableKey, value: next.value, na: next.na, updatedAt: null }];
}

/** The mark held for one criterion, or undefined when it has never been marked. */
export function markFor(
  marks: readonly MarkView[],
  criterionStableKey: string,
): MarkView | undefined {
  return marks.find((mark) => mark.criterionStableKey === criterionStableKey);
}
